/**
 * Block 12 PR-B Phase B3 Part C Step 25 — AnketaFormContainer.
 * Hotfix-2C Phase C5 Step 12 — anketaType-keyed Hybrid persistence.
 *
 * Layer 5 Container: URL guard + methodology fetch + state hooks + draft
 * hydration cascade + paywall handling + return-flow from cross-block pickers.
 *
 * **URL guard** (ADR-0020 §Q10): `:type` ∈ CALCULATOR_ANKETA_TYPES → otherwise
 * `<Navigate to="/calculator" replace />`.
 *
 * **Hydration cascade** (Hotfix-2C Phase C5):
 *   1. `?draftId=` present → projects IDB (UUID-keyed saved project resume).
 *   2. No draftId → Zustand `useCalculatorStore.getDraft(anketaType)` (sync,
 *      fast cross-route in-tab cache).
 *   3. Else → IDB `loadDraftFromIdb(anketaType)` (durable, survives F5
 *      refresh + browser restart).
 *   4. Else → blank `createInitialState`.
 *   Hydration dispatched via `actions.hydrateFromDraft` (reuses existing
 *   HYDRATE_FROM_DRAFT reducer action — recomputes validation against current
 *   refined schema, defensive против IDB shape drift).
 *
 * **Autosave (Phase C5):** на каждое state change useEffect debounced 300ms
 * → `setDraft(Zustand)` + `saveDraftToIdb(anketaType, state)`. Закрывает
 * Bug #2 Audit Round 3: state survives cross-navigation через FindPriceButton
 * (без `?draftId=` URL tracking) + F5 refresh.
 *
 * Co-existst с `useAnketaPersist` который пишет projects IDB по `state.projectId`
 * UUID (для `?draftId=` resume flow). Два layer'а по разным ключам:
 *   - projects store (UUID) — explicit saved projects.
 *   - calculator-drafts store (anketaType) — implicit "remember what I was doing".
 *
 * **Return-flow** (ADR-0020 §Q11): если URL содержит `?pickerFor=` + `?pickedId=`,
 * dispatches SET_BRAND + cleans URL (replace). Pattern для Block 2/5/8
 * cross-block selection cycle.
 *
 * **Deeplink** handler: forcePersist + navigate с `?returnTo=` URL-encoded.
 *
 * **5 P-021 states:** idle/loading/empty/error/success — каждый рендерит UI.
 * 403 SUBSCRIPTION_REQUIRED → PaywallModal (ADR-0014).
 *
 * Reference: ADR-0020 §Q8 (methodology), §Q10 (URL), §Q11 (deeplinks),
 * §Q6 (paywall); ADR-0019 §Q6, §Q11; ADR-0014 PaywallModal;
 * Block_12_hotfix2C_2026-05-17_start.md §6 (Phase C5 Steps 10-12).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isCalculatorAnketaType, type CalculatorAnketaType } from '@pomoshnik/shared';
import { getProject } from '@/lib/offline/idb/projects';
import { genUuid } from '@/lib/uuid';
import { useAuthStore } from '@/lib/stores/authStore';
import { PaywallModal } from '@/features/home/PaywallModal';
import { FreeAnketaFormContainer } from '../free/FreeAnketaFormContainer';
import { AnketaFormView } from './AnketaFormView';
import { computeMaterialRows } from './helpers/computeMaterialRows';
import { computeTotal } from './helpers/computeTotal';
import { selectErrorForPath } from './state/selectors';
import { useAnketaForm } from './useAnketaForm';
import { payloadToState, useAnketaPersist } from './useAnketaPersist';
import { useMethodology } from './useMethodology';
import { useCalculatorStore } from './state/useCalculatorStore';
import { clearDraftFromIdb, loadDraftFromIdb, saveDraftToIdb } from './state/calculatorIdbSync';
import { hasQuoteForProject } from '../quote/state/forwardChainLookup';
import type { AnketaParams, AnketaState } from './types';

/**
 * PR-calc-buttons-unify (Aibek 2026-06-17, ADR-0099):
 *   - partner: fix broken link `/partners-stub` → `/partners` (PartnersCategories,
 *     ADR-0080 Фаза D1, экран 2.8.1). Без `?category=` — user выбирает категорию сам.
 *   - furniture: fix `/master-stub` (broken) → `/furniture` (Block 5 FurnitureEntry,
 *     экран 2.5.6). PR #199 (ADR-0099) ошибочно вёл на placeholder-роут; исправлено
 *     в ADR-0100 (2026-06-22 amendment).
 *   - handles: новый тип. Row «Ручки» (computeMaterialRows.ts) ведёт сюда → Block 4
 *     KindPicker `/handles` (ручки и крючки в одном экране).
 */
const DEEPLINK_TARGETS: Record<'decor' | 'partner' | 'furniture' | 'handles', string> = {
  decor: '/decors',
  partner: '/partners',
  furniture: '/furniture',
  handles: '/handles',
};

function isSubscriptionRequiredError(err: Error): boolean {
  // useMethodology wraps API errors into Error; check message + cause for SUBSCRIPTION_REQUIRED.
  return /SUBSCRIPTION_REQUIRED|403/.test(err.message);
}

export function AnketaFormContainer() {
  const { type: typeParam } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL guard — invalid type → fallback Entry screen.
  if (!isCalculatorAnketaType(typeParam)) {
    return <Navigate to="/calculator" replace />;
  }
  const anketaType = typeParam;

  // PR-C Phase C7 — Other anketa использует отдельный FreeAnketaForm
  // component (ADR-0020 §Q9). FreeAnketaFormContainer сам resolve'ит
  // useSearchParams/useNavigate/draft hydration; outer Container делегирует
  // полностью. Inner AnketaFormContainerInner ловит остальные 7 типизированных
  // anketa через generic template engine.
  if (anketaType === 'other') {
    return <FreeAnketaFormContainer />;
  }

  return (
    <AnketaFormContainerInner
      anketaType={anketaType}
      searchParams={searchParams}
      navigate={navigate}
    />
  );
}

interface InnerProps {
  anketaType: CalculatorAnketaType;
  searchParams: URLSearchParams;
  navigate: ReturnType<typeof useNavigate>;
}

// Inner component — hooks все строго в порядке, no conditional hooks after URL guard early-return.
function AnketaFormContainerInner({ anketaType, searchParams, navigate }: InnerProps) {
  const draftId = searchParams.get('draftId');
  // PR-H3 (ADR-0028 §Решение #1) — explicit «новый проект» от CalculatorEntry
  // tile click. Disables Priority 2 (Zustand) + Priority 3 (IDB calculator-drafts)
  // hydration — user видит blank fields. Name генерируется lazily в useAnketaPersist.
  const isNewProject = searchParams.get('new') === 'true';
  const pickerFor = searchParams.get('pickerFor');
  const pickedId = searchParams.get('pickedId');

  const projectIdRef = useRef(draftId ?? genUuid());
  const [showPaywall, setShowPaywall] = useState(false);

  // Hotfix-2C Phase C5 Step 12 — hydration cascade gate. Start `false` always;
  // useEffect ниже resolve'ит cascade (?new=true → blank → ?draftId= → Zustand → IDB → blank).
  const [draftLoaded, setDraftLoaded] = useState(false);

  // Methodology fetch.
  const methState = useMethodology(anketaType);

  // Always-call hooks (React rule of hooks — same order across renders).
  // useAnketaForm запускается без initialDraft — hydration через actions.hydrateFromDraft
  // ниже после mount (lazy init only runs once, async cascade resolves post-mount).
  const { state, actions, validate } = useAnketaForm(anketaType, projectIdRef.current);
  const userId = useAuthStore((s) => s.profile?.phone) ?? 'anon-user';
  const methodology = methState.status === 'success' ? methState.data : null;
  // PR-H3 — pass `actions.setProjectName` для LAZY auto-naming (ADR-0028 §Решение #3).
  // useAnketaPersist детектит empty projectName и генерирует «{TypeLabel} №N» на
  // первом IDB save (snapshot.isDirty=true). Tap-and-exit без edits — name НЕ генерируется,
  // drafts list остаётся чистым.
  const { forcePersist, persistStatus } = useAnketaPersist(
    state,
    userId,
    methodology,
    300,
    actions.setProjectName,
  );

  // Phase C5 Step 12 — hydration cascade.
  //
  // Priority order (PR-H3 ADR-0028 inserted Priority 0):
  //   0. ?new=true + no ?draftId= → blank-by-default mode, skip Priority 1-3
  //      hydration entirely (explicit «новый проект» от CalculatorEntry tile click).
  //   1. ?draftId= → projects IDB (saved project resume, ADR-0019 §Q11.2).
  //   2. Zustand getDraft(anketaType) — fast sync in-tab cache (cross-route nav).
  //   3. IDB loadDraftFromIdb(anketaType) — durable cross-session draft.
  //   4. Blank (useAnketaForm initial state).
  //
  // Dispatched через `actions.hydrateFromDraft` (existing HYDRATE_FROM_DRAFT
  // reducer action — recomputes validation against current refined schema,
  // ADR-0020 §Q7 defensive против IDB shape drift между app versions).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // PR-H3 (ADR-0028 §Решение #1) — Priority 0: blank-by-default mode.
      // `?new=true` от CalculatorEntry tile click. Skip Priority 2/3 hydration
      // полностью; user видит blank fields. `?draftId=` имеет приоритет (если
      // оба flags present одновременно — explicit resume wins, treat как Priority 1).
      //
      // PR-H3 Round 2 F1 (cc-error-patterns Type C — Transient signal, R3):
      // `?new=true` — entry-only flag, не persistent intent. Clear из URL immediately,
      // иначе browser back из КП возвращает stale flag → Priority 0 снова blank → user
      // теряет данные (Finding 1, 28.05.2026 mobile verify).
      if (isNewProject && !draftId) {
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          url.searchParams.delete('new');
          window.history.replaceState({}, '', url.pathname + url.search);
        }
        setDraftLoaded(true);
        return;
      }

      // Priority 1 — explicit ?draftId= resume.
      if (draftId) {
        try {
          const record = await getProject(draftId);
          if (cancelled) return;
          if (record && record.data && record.data.params) {
            // PR-H3 Round 2 F2 (cc-error-patterns Type B Roundtrip + A.4 Homogeneous):
            // Раньше здесь были hardcoded empty maps для всего auxiliary state
            // (materialPrices, additional/filling/lighting toggles+qty+prices) →
            // user терял цены/доп при reopen `?draftId=` (Finding 2, 28.05.2026).
            // `payloadToState` восстанавливает symmetry с `encodeAuxiliaryRows` save
            // path + спредит результат сюда. brandSelections legacy corpusBrand merge
            // делается внутри payloadToState (PR-H1 R2 ADR-0026 pattern).
            const restored = payloadToState(record.data, anketaType);
            const hydrated: AnketaState = {
              anketaType,
              projectId: draftId,
              projectName: record.name,
              params: record.data.params as AnketaParams,
              materialOverrides: {},
              materialPrices: restored.materialPrices ?? {},
              brandSelections: restored.brandSelections ?? {},
              additionalToggles: restored.additionalToggles ?? {},
              additionalQuantities: restored.additionalQuantities ?? {},
              additionalPrices: restored.additionalPrices ?? {},
              additionalSelections: restored.additionalSelections ?? {},
              additionalLengths: restored.additionalLengths ?? {},
              additionalHeights: restored.additionalHeights ?? {},
              additionalPricesPerM2: restored.additionalPricesPerM2 ?? {},
              fillingToggles: restored.fillingToggles ?? {},
              fillingQuantities: restored.fillingQuantities ?? {},
              fillingPrices: restored.fillingPrices ?? {},
              lightingEnabled: restored.lightingEnabled ?? false,
              lightingQuantities: restored.lightingQuantities ?? {},
              lightingPrices: restored.lightingPrices ?? {},
              consumableToggles: {},
              consumableQuantities: {},
              consumablePrices: {},
              serviceToggles: {},
              serviceOverrides: {},
              servicePrices: {},
              customRows: [],
              validation: { isValid: false, errors: [] },
              isDirty: false,
              createdAt: record.createdAt,
              updatedAt: record.updatedAt,
            };
            actions.hydrateFromDraft(hydrated);
          }
        } catch (err) {
          if (import.meta.env.DEV) {
            console.warn('[AnketaFormContainer] draftId hydration failed', err);
          }
        }
        setDraftLoaded(true);
        return;
      }

      // Priority 2 — anketaType-keyed Zustand cache (sync, fast path).
      const fromZustand = useCalculatorStore.getState().getDraft(anketaType);
      if (fromZustand) {
        projectIdRef.current = fromZustand.projectId;
        actions.hydrateFromDraft(fromZustand);
        setDraftLoaded(true);
        return;
      }

      // Priority 3 — anketaType-keyed IDB durable mirror.
      try {
        const fromIdb = await loadDraftFromIdb(anketaType);
        if (cancelled) return;
        if (fromIdb) {
          projectIdRef.current = fromIdb.projectId;
          useCalculatorStore.getState().setDraft(anketaType, fromIdb);
          actions.hydrateFromDraft(fromIdb);
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[AnketaFormContainer] anketaType hydration failed', err);
        }
      }
      setDraftLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [draftId, isNewProject, anketaType, actions]);

  // Phase C5 Step 12 — anketaType-keyed autosave (300ms debounce).
  //
  // Co-existst с useAnketaPersist (projects IDB по state.projectId UUID).
  // Этот слой решает Bug #2 Audit Round 3: state survives cross-navigation
  // через `<FindPriceButton>` (без ?draftId= URL) + F5 refresh.
  //
  // Skip pristine state same как useAnketaPersist (isDirty || projectName).
  // 300ms tail-debounce: каждое state change resets timer; save fires только
  // после тишины. Cleanup отменяет pending timer на unmount.
  const setDraftZustand = useCalculatorStore((s) => s.setDraft);
  useEffect(() => {
    if (!state.isDirty && state.projectName === '') return;
    const timer = setTimeout(() => {
      setDraftZustand(anketaType, state);
      void saveDraftToIdb(anketaType, state);
    }, 300);
    return () => clearTimeout(timer);
  }, [state, anketaType, setDraftZustand]);

  const materialRows = useMemo(
    () => (methState.status === 'success' ? computeMaterialRows(state, methState.data) : []),
    [state, methState],
  );
  const total = useMemo(
    () => (methState.status === 'success' ? computeTotal(state, methState.data) : 0),
    [state, methState],
  );

  // Return-flow: если picker вернул выбранный item → dispatch + clean URL.
  useEffect(() => {
    if (pickerFor && pickedId) {
      actions.setBrand(pickerFor, pickedId);
      navigate(`/calculator/anketa/${anketaType}?draftId=${projectIdRef.current}`, {
        replace: true,
      });
    }
  }, [pickerFor, pickedId, actions, anketaType, navigate]);

  // Detect paywall на error из useMethodology.
  useEffect(() => {
    if (methState.status === 'error' && isSubscriptionRequiredError(methState.error)) {
      setShowPaywall(true);
    }
  }, [methState]);

  const onDeeplink = useCallback(
    async (rowId: string, target: 'decor' | 'partner' | 'furniture' | 'handles') => {
      await forcePersist();
      const returnTo = `/calculator/anketa/${anketaType}?draftId=${projectIdRef.current}&pickerFor=${rowId}`;
      navigate(`${DEEPLINK_TARGETS[target]}?returnTo=${encodeURIComponent(returnTo)}`);
    },
    [forcePersist, anketaType, navigate],
  );

  // Block 12 Phase B5 Step 2 — КП CTA wire (closes OQ-5, ADR-0020 §Q11).
  // forcePersist гарантирует last debounced state в IDB до navigate, чтобы
  // PR-D КП builder поднял свежий snapshot по projectId. Pattern mirrors
  // onDeeplink выше (same forcePersist→navigate sequence).
  // `encodeURIComponent` defensive: `projectIdRef.current` обычно `genUuid()`
  // (safe), но при ?draftId= seed приходит из URL — encode защищает от пробросов.
  //
  // PR-H1 #1 fix — synchronously sync anketaType-keyed layer (Zustand + IDB)
  // перед navigate. Без этого debounced autosave (line 239-246, 300ms timeout)
  // отменяется unmount cleanup при navigate-induced re-render, и на browser
  // back к `/calculator/anketa/<type>` (без `?draftId=`) hydration cascade
  // Priority 2 (Zustand) + Priority 3 (IDB) пустые → blank state → user видит
  // потерянную анкету.
  //
  // PR-H3 (ADR-0028) reorder: setDraft Zustand FIRST (sync), затем `await forcePersist`.
  // Раньше Zustand setDraft был ПОСЛЕ await forcePersist; PR-H3 LAZY auto-naming
  // добавил `await generateProjectName` в persistNow chain → setDraft fired позже,
  // ломая тест синхронности PR-H1. Reorder сохраняет PR-H1 intent (Zustand sync)
  // и PR-H3 (lazy gen в projects IDB layer).
  const onCreateQuote = useCallback(async () => {
    // Sync Zustand FIRST — primary source для browser-back hydration Priority 2.
    // Не зависит от async forcePersist work (PR-H3 lazy name gen + saveProject).
    useCalculatorStore.getState().setDraft(anketaType, state);
    // IDB calculator-drafts save fire-and-forget (mirror autosave pattern line 243).
    void saveDraftToIdb(anketaType, state);
    // Projects IDB write (async, includes PR-H3 lazy name generation).
    await forcePersist();
    navigate(`/calculator/quote?projectId=${encodeURIComponent(projectIdRef.current)}`);
  }, [forcePersist, state, anketaType, navigate]);

  // PR-C1 (ADR-0035) Phase 1 — forward-chain «Вперёд» → существующий КП.
  // Видна ТОЛЬКО если для projectId уже создан КП (saved quote ∥ quote draft).
  // Async-проверка IDB живёт здесь (контейнер, ADR-0005), не в SectionTotal.
  // Re-check после hydration (projectIdRef финализируется до draftLoaded) и при
  // смене state.projectId (return-flow picker). Чистое чтение IDB (R2).
  const [forwardVisible, setForwardVisible] = useState(false);
  useEffect(() => {
    if (!draftLoaded) return;
    let cancelled = false;
    void hasQuoteForProject(projectIdRef.current).then((has) => {
      if (!cancelled) setForwardVisible(has);
    });
    return () => {
      cancelled = true;
    };
  }, [draftLoaded, state.projectId]);

  // onForward переиспользует onCreateQuote: тот же persist-before-navigate на
  // `/calculator/quote?projectId=<id>` (R3 — existing persistent-маршрут). Persist
  // гарантирует, что ADR-0027 invalidation увидит свежую анкету (journey #2:
  // изменил цену → «Вперёд» → КП показывает новую цену). Кнопка лишь differs
  // видимостью (forwardVisible) — открывает existing КП, не создаёт новый.
  const onForward = onCreateQuote;

  // Phase C5 Step 12 — clear anketaType-keyed drafts после successful save.
  // Zustand + IDB drafts — это «незавершённое» состояние. После save (явный
  // user-action — backend persist) draft уже не нужен; fresh visit (без ?draftId=)
  // получит blank state, а saved project resume идёт через ?draftId=. Поведение
  // mirrors `useAnketaPersist` (которое сохраняет projects IDB до конца сессии).
  const clearDraftZustand = useCalculatorStore((s) => s.clearDraft);
  const onSave = useCallback(async () => {
    if (!validate()) return;
    await forcePersist();
    clearDraftZustand(anketaType);
    await clearDraftFromIdb(anketaType);
    // Phase 9+: toast / navigate to project list. Now просто stay on page.
  }, [validate, forcePersist, clearDraftZustand, anketaType]);

  const errorForPath = useCallback((path: string) => selectErrorForPath(state, path), [state]);

  // Loading states (P-021).
  if (!draftLoaded || methState.status === 'idle' || methState.status === 'loading') {
    return (
      <p className="text-muted text-center p-8" role="status">
        Загрузка калькулятора...
      </p>
    );
  }

  if (methState.status === 'empty') {
    return (
      <p className="text-muted text-center p-8" role="status">
        Методика для этого типа анкеты не найдена.
      </p>
    );
  }

  if (methState.status === 'error') {
    if (showPaywall) {
      return (
        <PaywallModal
          onPay={() => navigate('/profile')}
          onClose={() => {
            setShowPaywall(false);
            navigate('/calculator');
          }}
        />
      );
    }
    return (
      <p className="text-error text-center p-8" role="alert">
        Ошибка загрузки: {methState.error.message}
      </p>
    );
  }

  const validationErrorCount = state.validation.errors.length;
  const isSaving = persistStatus === 'saving';

  return (
    <AnketaFormView
      state={state}
      methodology={methState.data}
      materialRows={materialRows}
      total={total}
      isValid={state.validation.isValid}
      validationErrorCount={validationErrorCount}
      actions={actions}
      isSaving={isSaving}
      onSave={onSave}
      onCreateQuote={onCreateQuote}
      onForward={onForward}
      forwardVisible={forwardVisible}
      onDeeplink={onDeeplink}
      errorForPath={errorForPath}
    />
  );
}
